import { Disc, Mic, MicOff, Pause, Play, Square, Upload, X } from 'lucide-react';
import { useI18n } from '../../lib/i18n';
import { fmtElapsed } from '../../lib/share/peer';
import { fmtRemaining } from '../../lib/rec/uploader';
import { useRec } from './RecProvider';
import { surfaceLabel } from './RecordControl';

/**
 * LA PÍLDORA REC, fija bajo la barra (spec §4.12.1 punto 5): "REC 04:12 · Pause · Stop". Junto con
 * la barra nativa del navegador y la ventana de control, nadie graba sin saberlo. Cuando el
 * navegador bloqueó la ventana de control, esta píldora ES el control.
 *
 * Tras Stop, si se cerró la vista previa con la subida en curso, la píldora dice "Uploading a/b…"
 * y reabre la vista previa al clic.
 */
export function RecPill() {
  const { t } = useI18n();
  const r = useRec();

  if (r.phase === 'preview' && !r.previewOpen && r.preview) {
    const u = r.upload;
    const st = r.preview.status;
    const text = st === 'uploaded' ? t('rec.uploaded')
      : st === 'failed' ? t('rec.failedUpload')
        : u ? `${t('rec.uploadingParts', { a: u.partsDone, b: Math.max(u.partsQueued, 1) })}${u.remainingSec != null && u.remainingSec > 0 ? ` · ${t('rec.remaining', { t: fmtRemaining(u.remainingSec) })}` : ''}` : t('rec.uploadingParts', { a: 0, b: 1 });
    return (
      <button type="button" className={`ss-pill rec-pill upload${st === 'failed' ? ' failed' : ''}`} onClick={r.openPreview} title={t('rec.previewTitle')}>
        <Upload size={13} strokeWidth={2} aria-hidden />
        <span>{text}</span>
      </button>
    );
  }

  if (r.phase !== 'recording' && r.phase !== 'countdown' && r.phase !== 'starting') return null;
  const paused = !!r.pausedBy;
  const cancel = () => r.cancel();
  return (
    <div className={`ss-pill live rec-pill${paused ? ' paused' : ''}`} role="status">
      <span className={`rec-dot${paused ? ' paused' : ''}`} aria-hidden><Disc size={11} strokeWidth={3} /></span>
      <b>{t('rec.rec')}</b>
      <span className="ss-mono">{r.phase === 'countdown' ? String(r.countdown) : r.phase === 'starting' ? '…' : fmtElapsed(r.elapsedMs)}</span>
      {r.phase === 'recording' && <span className="ss-pill-note">{t('rec.recordingLabel', { surface: surfaceLabel(r.surface, t) })}</span>}
      {r.upload?.failed && <span className="ss-pill-note warn">{t('rec.uploadStuck')}</span>}
      {r.pausedBy === 'net' && !r.upload?.failed && <span className="ss-pill-note warn">{t('rec.pausedNet')}</span>}
      {r.limitSoon && !paused && <span className="ss-pill-note warn">{t('rec.limitSoon')}</span>}
      {r.micState === 'denied' && <span className="ss-pill-note warn">{t('share.micDenied')}</span>}
      {r.phase === 'recording' && (paused
        ? <button type="button" className="btn ghost sm" onClick={r.resume} disabled={r.pausedBy === 'net'}><Play size={14} strokeWidth={2} /> {t('rec.resume')}</button>
        : <button type="button" className="btn ghost sm" onClick={r.pause}><Pause size={14} strokeWidth={2} /> {t('rec.pause')}</button>)}
      {r.phase === 'recording' && r.hasMic && (
        <button type="button" className={`btn ghost sm${r.muted ? ' is-muted' : ''}`} onClick={r.toggleMute} aria-pressed={r.muted} title={r.muted ? t('share.unmuteMic') : t('share.mute')}>
          {r.muted ? <MicOff size={14} strokeWidth={2} /> : <Mic size={14} strokeWidth={2} />}
        </button>
      )}
      {r.phase === 'recording' && r.upload?.failed && <button type="button" className="btn ghost sm" onClick={r.retryUpload} title={t('rec.uploadStuck')}><Upload size={13} strokeWidth={2} /> {t('rec.retryUpload')}</button>}
      {r.phase === 'recording' && <button type="button" className="btn ghost sm ss-stop" onClick={r.stop}><Square size={13} strokeWidth={2.5} /> {t('rec.stop')}</button>}
      <button type="button" className="btn ghost sm" onClick={cancel} title={t('rec.cancel')} aria-label={t('rec.cancel')}><X size={14} strokeWidth={2} /></button>
    </div>
  );
}
